

















































import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import DraggableIcon from './DraggableIcon.vue';
import { GroupLevelAttributeValue } from '@/jbi-shared/types/jaas-group-level-attributes.types';
import AttributeActionButtons from './AttributeActionButtons.vue';
import { capitalize } from '@/jbi-shared/util/string.utils';

@Component({
  components: {
    DraggableIcon,
    AttributeActionButtons
  }
})
export default class GroupLevelAttributeNumberField extends Vue {
  @Prop() attributeValue!: GroupLevelAttributeValue;
  @Prop() fieldClasses!: any;
  @Prop() duplicationError!: boolean;
  @Prop({ default: false }) isSettings!: boolean;
  @Prop({ default: false }) isCourseSizeGLA!: boolean;
  @Prop({ default: false }) overrideDisableDraggable!: boolean;
  @Prop({ default: false }) overrideDisableAttributeButton!: boolean;
  @Prop() deleteAttribute!: () => void;
  @Prop() updateAttributeValue!: (attribute: GroupLevelAttributeValue) => void;
  @Prop() toggleAttributeRequire!: (
    attribute: GroupLevelAttributeValue
  ) => void;
  @Prop() editAttribute!: (attribute: GroupLevelAttributeValue) => void;

  fieldErrorMsg: string = '';
  private readonly maxValue: number = 9999;

  // Computed property so changes from the value from parent component are synced
  get isRequired() {
    return this.attributeValue.groupLevelAttributeSpec.isRequired;
  }

  get placeholder() {
    return capitalize(this.attributeValue.groupLevelAttributeType.type);
  }

  sanitizeInput(newValue: string): void {
    // Use a regular expression to remove anything that is not a digit.
    let sanitized = newValue.replace(/\D/g, '');

    // Use a regex to handle the leading zero.
    //   - ^0: Matches a '0' at the start of the string.
    //   - (\d+): Captures one or more digits that follow the leading zero.
    // The replacement '$1' refers to the captured group (the digits after the '0').
    // This effectively replaces "0123" with "123".
    // It won't match a single "0", so that case is safe.
    sanitized = sanitized.replace(/^0(\d+)/, '$1');

    // Check if the sanitized value exceeds the max value, but only if it is course size GLA
    if (sanitized && this.isCourseSizeGLA) {
      const numericValue = parseInt(sanitized, 10);
      if (numericValue > this.maxValue) {
        // If the value is too large, clamp it to the max value.
        sanitized = this.maxValue.toString();
      }
    }

    // Check if the sanitized value is different from the current value.
    // This is important to prevent an infinite loop of updates.
    if (newValue !== sanitized) {
      // Temporarily set the model to the invalid value so Vue sees a change.
      this.attributeValue.value = newValue;

      // Then, in the next DOM update cycle, force it back to the sanitized value.
      this.$nextTick(() => {
        this.attributeValue.value = sanitized;
      });
    } else {
      this.attributeValue.value = sanitized;
    }
  }

  checkDigit(event: KeyboardEvent): void {
    // Allow control keys that don't produce a character
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End'
    ];

    if (allowedKeys.includes(event.key)) {
      return; // Do not prevent default for these keys
    }

    // Allow command/control + A, C, V, X for select all, copy, paste, cut
    if (
      (event.ctrlKey || event.metaKey) &&
      ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())
    ) {
      return;
    }

    // If the key is not a digit, prevent it.
    // The regex /^\d$/ checks if the key is a single digit (0-9).
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  @Watch('isRequired', { immediate: true })
  updateErrorState() {
    if (this.attributeValue.value) {
      this.validateRequiredFieldValues();
      return;
    }

    this.attributeValue.hasFieldError = this.isRequired;
    this.updateAttributeValue(this.attributeValue);
  }

  @Watch('attributeValue.value')
  validateRequiredFieldValues() {
    // validate null / empty values for required fields
    if (
      this.attributeValue.groupLevelAttributeSpec.isRequired &&
      (this.attributeValue.value === null || this.attributeValue.value === '')
    ) {
      this.fieldErrorMsg = `${this.attributeValue.label} is required.`;
      this.attributeValue.hasFieldError = true;
      this.updateAttributeValue(this.attributeValue);
      return;
    }

    this.fieldErrorMsg = '';
    this.attributeValue.hasFieldError = false;
    this.updateAttributeValue(this.attributeValue);
  }
}
